import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Shield, Mountain, BookOpen, FileText, Briefcase, Users, Activity, CheckCircle, Layers, Calculator, Mail, Link as LinkIcon, Play, Building2, ClipboardCheck, Compass, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getComplianceCoreUrl } from "@/config/domains";
import SkipLink from "@/components/ui/SkipLink";
import MegaMenuPlatform from "@/components/navigation/MegaMenuPlatform";
import MegaMenuSolutions from "@/components/navigation/MegaMenuSolutions";
import MegaMenuResources from "@/components/navigation/MegaMenuResources";
import MegaMenuCompany from "@/components/navigation/MegaMenuCompany";
import BrandBar from "@/components/navigation/BrandBar";
import GeoSuggestionBanner from "@/components/GeoSuggestionBanner";

import LocaleLink from "@/components/LocaleLink";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Mountain silhouette SVG component
const MountainSilhouette = () => (
  <svg
    className="absolute bottom-0 left-0 right-0 w-full h-4 opacity-20"
    viewBox="0 0 1440 40"
    preserveAspectRatio="none"
    fill="currentColor"
  >
    <path d="M0,40 L0,25 L120,15 L200,22 L280,8 L360,18 L440,5 L520,20 L600,10 L680,25 L760,12 L840,20 L920,6 L1000,22 L1080,14 L1160,24 L1240,10 L1320,20 L1440,15 L1440,40 Z" />
  </svg>
);

type MobileLink = { to: string; label: string };

const MobileLinkGrid = ({ items, onClose }: { items: MobileLink[]; onClose: () => void }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 px-1 pb-2">
    {items.map((it) => {
      const cls = "p-3 text-sm text-white/80 hover:text-white transition-all duration-150 rounded-lg hover:bg-white/20 hover:border-l-2 hover:border-primary min-h-[44px] flex items-center touch-manipulation";
      return it.to.startsWith('https://') ? (
        <a key={it.to} href={it.to} onClick={onClose} className={cls}>{it.label}</a>
      ) : (
        <LocaleLink key={it.to} to={it.to} onClick={onClose} className={cls}>{it.label}</LocaleLink>
      );
    })}
  </div>
);

const MobileSubGroup = ({
  title,
  items,
  open,
  onToggle,
  onClose,
}: {
  title: string;
  items: MobileLink[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) => (
  <div className="border-b border-white/10 last:border-b-0">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-3 min-h-[44px] text-left text-sm font-medium text-white/85 hover:text-white touch-manipulation"
      aria-expanded={open}
    >
      <span className="uppercase tracking-wide text-xs">{title}</span>
      <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <MobileLinkGrid items={items} onClose={onClose} />}
  </div>
);

const NavigationMarketing = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (k: string) => setOpenGroups((p) => ({ ...p, [k]: !p[k] }));
  const { t } = useTranslation();

  return (
    <>
      <SkipLink />
      <GeoSuggestionBanner />
      <div className="fixed top-0 left-0 right-0 z-50">
        <nav 
          className="bg-brand-ink/95 backdrop-blur-sm border-b border-white/10"
          role="navigation"
          aria-label={t('common.mainNavigation')}
        >
        {/* Mountain silhouette at the bottom */}
        <MountainSilhouette />

        {/* Utility tier, inside this surface rather than a separate light bar
            above it: one header instead of two, 114px of chrome down to 78px. */}
        <div className="container mx-auto px-4 lg:px-6 relative">
          <BrandBar />
        </div>

        <div className="container mx-auto px-4 lg:px-6 relative">
        <div className="flex min-h-[52px] items-center justify-between py-1">
          <LocaleLink to="/" className="flex items-center hover:opacity-80 transition-opacity flex-shrink-0">
            <img 
              src="/assets/logos/csquare_logo_horizontal_white_sm.webp" 
              alt="cSquare GRC - Autonomous Compliance" 
              className="h-8 md:h-9 lg:h-[38px] w-auto"
              width={140}
              height={56}
            />
          </LocaleLink>
          
          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-6">
            
            {/* Platform Mega Menu */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="font-medium text-white/90 flex items-center gap-1 text-base px-3 py-2 cursor-pointer hover:text-white transition-colors bg-transparent border-none">
                  {t('navigation.platform')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-background border border-border shadow-[0_18px_40px_hsl(var(--brand-ink)/0.16)] z-[60] p-0 rounded-none">
                <MegaMenuPlatform />
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Solutions Mega Menu */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="font-medium text-white/90 flex items-center gap-1 text-base px-3 py-2 cursor-pointer hover:text-white transition-colors bg-transparent border-none">
                  {t('navigation.solutions')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-background border border-border shadow-[0_18px_40px_hsl(var(--brand-ink)/0.16)] z-[60] p-0 rounded-none">
                <MegaMenuSolutions />
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Pricing */}
            <LocaleLink to="/pricing" className="font-medium text-white/90 hover:text-white transition-colors text-base px-3 py-2">
              {t('navigation.pricing')}
            </LocaleLink>

            {/* Resources Mega Menu */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="font-medium text-white/90 flex items-center gap-1 text-base px-3 py-2 cursor-pointer hover:text-white transition-colors bg-transparent border-none">
                  {t('navigation.resources')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-background border border-border shadow-[0_18px_40px_hsl(var(--brand-ink)/0.16)] z-[60] p-0 rounded-none">
                <MegaMenuResources />
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Company Dropdown */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="font-medium text-white/90 flex items-center gap-1 text-base px-3 py-2 cursor-pointer hover:text-white transition-colors bg-transparent border-none">
                  {t('navigation.company')}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-background border border-border shadow-[0_18px_40px_hsl(var(--brand-ink)/0.16)] z-[60] p-0 rounded-none">
                <MegaMenuCompany />
              </DropdownMenuContent>
            </DropdownMenu>



            {/* Book a Demo */}
            <Button 
              asChild
              size="sm"
              variant="outline"
              className="font-medium border-white/30 text-white hover:bg-white hover:text-foreground"
            >
              <LocaleLink to="/book-demo">
                {t('navigation.bookDemo')}
              </LocaleLink>
            </Button>

            {/* Get Started Free */}
            <Button 
              asChild
              size="sm"
              variant="cta"
              className="font-medium whitespace-nowrap"
            >
              <LocaleLink to="/signup?source=nav-desktop">
                {t('navigation.getStartedFree')}
              </LocaleLink>
            </Button>

          </div>

          <div className="xl:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? t('common.closeMenu') : t('common.openMenu')}
              aria-expanded={isOpen}
              className="p-3 min-h-[44px] min-w-[44px] touch-manipulation text-white hover:bg-white/10"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="xl:hidden mt-4 border-t border-white/10 flex flex-col max-h-[calc(100vh-80px)]">
            <div className="pt-4 overflow-y-auto flex-1 pb-2">
              <div className="grid grid-cols-2 gap-2">
                <LocaleLink to="/pricing" className="font-medium text-white/90 hover:text-white transition-all duration-150 p-3 rounded-lg hover:bg-white/20 hover:border-l-2 hover:border-primary min-h-[44px] flex items-center touch-manipulation" onClick={() => setIsOpen(false)}>
                  {t('navigation.pricing')}
                </LocaleLink>
                <LocaleLink to="/book-demo" className="font-medium text-white/90 hover:text-white transition-all duration-150 p-3 rounded-lg hover:bg-white/20 hover:border-l-2 hover:border-primary min-h-[44px] flex items-center touch-manipulation" onClick={() => setIsOpen(false)}>
                  {t('navigation.bookDemo')}
                </LocaleLink>
              </div>

              {/* Platform */}
              <p className="font-medium text-white/70 px-3 pt-4 pb-2 text-xs uppercase tracking-wide">{t('navigation.platform')}</p>
              <LocaleLink to="/whats-new?v=v1.3" onClick={() => setIsOpen(false)} className="mx-1 mb-2 flex items-center justify-between gap-2 p-3 rounded-lg bg-primary/15 border border-primary/30 hover:bg-primary/25 transition-colors min-h-[44px] touch-manipulation">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider bg-primary text-primary-foreground shrink-0">{t('megaMenu.latestPill')}</span>
                  <span className="text-xs text-white truncate"><span className="font-semibold">{t('megaMenu.releaseVersion')}</span>, {t('megaMenu.releaseTitle')}</span>
                </div>
                <span className="text-white/80 shrink-0">→</span>
              </LocaleLink>
              <div className="mx-1 rounded-lg bg-white/5 border border-white/10">
                <MobileSubGroup
                  title={t('megaMenu.ai')}
                  open={!!openGroups.platAi}
                  onToggle={() => toggleGroup('platAi')}
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/features', label: t('megaMenu.aiAgents') },
                    { to: '/features#assistant', label: t('megaMenu.csquareAssistant') },
                    { to: '/integrations/continuous-monitoring', label: t('megaMenu.continuousMonitoring') },
                    { to: '/features#workflows', label: t('megaMenu.dagWorkflows') },
                  ]}
                />
                <MobileSubGroup
                  title={t('megaMenu.intelligenceRisk')}
                  open={!!openGroups.platIntel}
                  onToggle={() => toggleGroup('platIntel')}
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/features#regulatory-intelligence', label: t('megaMenu.regulatoryIntelligence') },
                    { to: '/features#document-intelligence', label: t('megaMenu.documentIntelligence') },
                    { to: '/advanced-analytics', label: t('megaMenu.advancedAnalytics') },
                    { to: '/enterprise-risk', label: t('megaMenu.enterpriseRisk') },
                  ]}
                />
                <MobileSubGroup
                  title={t('megaMenu.complianceOps')}
                  open={!!openGroups.platOps}
                  onToggle={() => toggleGroup('platOps')}
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/automated-control-mapping', label: t('megaMenu.automatedControlMapping') },
                    { to: '/control-applicability-wizard', label: t('megaMenu.controlApplicabilityWizard') },
                    { to: '/enterprise-audit', label: t('megaMenu.enterpriseAudit') },
                    { to: '/trust', label: t('megaMenu.trustCenter') },
                  ]}
                />
                <MobileSubGroup
                  title={t('megaMenu.coverageEcosystem')}
                  open={!!openGroups.platCov}
                  onToggle={() => toggleGroup('platCov')}
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/framework-library', label: t('megaMenu.frameworkLibrary') },
                    { to: '/integrations', label: t('megaMenu.integrations') },
                    { to: '/see-what-applies', label: t('megaMenu.seeWhatApplies') },
                    { to: '/global-coverage', label: t('megaMenu.globalCoverage') },
                    { to: '/platform-tour', label: t('megaMenu.platformTour') },
                  ]}
                />
              </div>
              <div className="mt-2">
                <MobileLinkGrid
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/ai-grc-platform', label: t('megaMenu.aiGrcPlatform') },
                    { to: '/our-approach', label: t('megaMenu.ourApproach') },
                    { to: '/why-csquare', label: t('megaMenu.whyCsquare') },
                  ]}
                />
              </div>

              {/* Solutions */}
              <p className="font-medium text-white/70 px-3 pt-4 pb-2 text-xs uppercase tracking-wide">{t('navigation.solutions')}</p>
              <div className="mx-1 rounded-lg bg-white/5 border border-white/10">
                <MobileSubGroup
                  title={t('megaMenu.byIndustry')}
                  open={!!openGroups.solInd}
                  onToggle={() => toggleGroup('solInd')}
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/for/saas', label: t('megaMenu.industrySaas') },
                    { to: '/for/fintech', label: t('megaMenu.industryFintech') },
                    { to: '/for/financial-services', label: t('megaMenu.industryFinancialServices') },
                    { to: '/for/healthcare', label: t('megaMenu.industryHealthcare') },
                    { to: '/for/public-sector', label: t('megaMenu.industryPublicSector') },
                  ]}
                />
                <MobileSubGroup
                  title={t('megaMenu.byCompanySize')}
                  open={!!openGroups.solSize}
                  onToggle={() => toggleGroup('solSize')}
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/for/small-business', label: t('megaMenu.sizeSmallBusiness') },
                    { to: '/for/mid-market', label: t('megaMenu.sizeMidMarket') },
                    { to: '/enterprise', label: t('megaMenu.sizeEnterprise') },
                    { to: '/regulated-enterprises', label: t('megaMenu.sizeRegulated') },
                  ]}
                />
                <MobileSubGroup
                  title={t('megaMenu.byAudience')}
                  open={!!openGroups.solAud}
                  onToggle={() => toggleGroup('solAud')}
                  onClose={() => setIsOpen(false)}
                  items={[
                    { to: '/use-cases?role=ciso', label: t('megaMenu.roleCiso') },
                    { to: '/use-cases?role=cco', label: t('megaMenu.roleCco') },
                    { to: '/use-cases?role=legal', label: t('megaMenu.roleLegal') },
                    { to: '/career-transition', label: t('megaMenu.careerTransition') },
                    { to: '/why-csquare', label: t('megaMenu.whyCsquareSolutions') },
                  ]}
                />
              </div>

              {/* Resources */}
              <p className="font-medium text-white/70 px-3 pt-4 pb-2 text-xs uppercase tracking-wide">{t('navigation.resources')}</p>
              <MobileLinkGrid
                onClose={() => setIsOpen(false)}
                items={[
                  { to: '/readiness', label: t('navigation.readinessAssessment') },
                  { to: '/assessment', label: t('navigation.complianceAssessment') },
                  { to: '/ripe-assessment', label: t('navigation.ripeAssessment') },
                  { to: '/roi-calculator', label: t('navigation.roiCalculator') },
                  { to: '/insights', label: t('navigation.insights') },
                  { to: '/resources', label: t('navigation.resourceCenter') },
                  { to: '/faq', label: t('navigation.faq') },
                  { to: 'https://docs.csquaregrc.com/', label: t('megaMenu.docs') },
                  { to: '/hikers-mindset', label: t('megaMenu.hikersMindset') },
                ]}
              />

              {/* Events */}
              <p className="font-medium text-white/70 px-3 pt-4 pb-2 text-xs uppercase tracking-wide">{t('megaMenu.events')}</p>
              <MobileLinkGrid
                onClose={() => setIsOpen(false)}
                items={[
                  { to: '/demo-days', label: t('megaMenu.demoDays') },
                  { to: '/webinars', label: t('megaMenu.webinars') },
                  { to: '/events', label: t('megaMenu.allEvents') },
                ]}
              />

              {/* Company */}
              <p className="font-medium text-white/70 px-3 pt-4 pb-2 text-xs uppercase tracking-wide">{t('navigation.company')}</p>
              <MobileLinkGrid
                onClose={() => setIsOpen(false)}
                items={[
                  { to: '/about', label: t('navigation.aboutCsquareMobile') },
                  { to: '/partners', label: t('navigation.partners') },
                  { to: '/careers', label: t('navigation.careers') },
                  { to: '/contact', label: t('navigation.contactUs') },
                  { to: '/trust', label: t('navigation.trustCenter') },
                  { to: '/newsroom', label: t('navigation.newsroom') },
                  { to: '/press-kit', label: t('navigation.pressKit') },
                ]}
              />
            </div>


            {/* Sticky CTAs */}
            <div className="pt-3 pb-4 border-t border-white/10 space-y-2 bg-brand-ink/95 flex-shrink-0">
              <a 
                href={getComplianceCoreUrl("/auth")}
                className="block font-medium text-white/90 hover:text-white transition-all duration-150 p-3 rounded-lg hover:bg-white/20 hover:border-l-2 hover:border-primary min-h-[44px] flex items-center touch-manipulation"
                onClick={() => setIsOpen(false)}
              >
                {t('navigation.signIn')}
              </a>
              <Button 
                asChild
                variant="cta"
                className="font-medium w-full min-h-[48px] text-base touch-manipulation"
                size="sm"
              >
                <LocaleLink
                  to="/signup?source=nav-mobile"
                  onClick={() => setIsOpen(false)}
                >
                  {t('navigation.getStartedFree')}
                </LocaleLink>
              </Button>
            </div>
          </div>
        )}
      </div>
      </nav>
      </div>
    </>
  );
};

export default NavigationMarketing;
